/**
 * DatabaseStats Component
 * Prikazuje statistiku iz baze - ukupno proizvoda, dostupni, dobavljači, duplikati
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  CheckCircle as AvailableIcon,
  LocalShipping as SupplierIcon,
  ContentCopy as DuplicateIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabase';
import { adminApi } from '../../../lib/adminApi';

interface Stats {
  totalProducts: number;
  availableProducts: number;
  suppliers: { name: string; count: number }[];
  duplicateSkus: { sku: string; count: number }[];
}

export function DatabaseStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandSuppliers, setExpandSuppliers] = useState(false);
  const [expandDuplicates, setExpandDuplicates] = useState(false);

  // Helper za dohvaćanje svih redova s paginacijom (Supabase ima limit 1000)
  const fetchAllRows = async <T,>(
    query: () => ReturnType<ReturnType<typeof supabase.from>['select']>
  ): Promise<T[]> => {
    const PAGE_SIZE = 1000;
    let allData: T[] = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await query().range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      if (data && data.length > 0) {
        allData = [...allData, ...(data as T[])];
        from += PAGE_SIZE;
        hasMore = data.length === PAGE_SIZE;
      } else {
        hasMore = false;
      }
    }

    return allData;
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      // Single server-side aggregation — much faster than paginating all rows.
      const agg = await adminApi.products.stats();

      const suppliers = Object.entries(agg.supplierCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      const duplicateSkus = agg.duplicateSkus.slice(0, 10); // Top 10

      setStats({
        totalProducts: agg.totalProducts || 0,
        availableProducts: agg.availableProducts || 0,
        suppliers,
        duplicateSkus,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (!stats) return null;

  const totalDuplicates = stats.duplicateSkus.reduce((sum, d) => sum + d.count - 1, 0);

  return (
    <Paper sx={{ p: 1.5, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Statistika baze
        </Typography>
        <IconButton size="small" onClick={loadStats} title="Osvježi">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Ukupno proizvoda */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          Ukupno proizvoda:
        </Typography>
        <Chip
          label={stats.totalProducts.toLocaleString()}
          size="small"
          color="primary"
          sx={{ fontWeight: 'bold' }}
        />
      </Box>

      {/* Dostupni proizvodi */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AvailableIcon sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          Dostupnih:
        </Typography>
        <Chip
          label={stats.availableProducts.toLocaleString()}
          size="small"
          color="success"
          sx={{ fontWeight: 'bold' }}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Dobavljači */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          p: 0.5,
          mx: -0.5,
        }}
        onClick={() => setExpandSuppliers(!expandSuppliers)}
      >
        <SupplierIcon sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          Dobavljači:
        </Typography>
        <Chip
          label={stats.suppliers.length}
          size="small"
          color="info"
          sx={{ fontWeight: 'bold' }}
        />
        {expandSuppliers ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={expandSuppliers}>
        <List dense sx={{ py: 0, pl: 3 }}>
          {stats.suppliers.map((s) => (
            <ListItem key={s.name} sx={{ py: 0, px: 0 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                      {s.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.count.toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Duplikati */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: stats.duplicateSkus.length > 0 ? 'pointer' : 'default',
          '&:hover': stats.duplicateSkus.length > 0 ? { bgcolor: 'action.hover' } : {},
          borderRadius: 1,
          p: 0.5,
          mx: -0.5,
        }}
        onClick={() => stats.duplicateSkus.length > 0 && setExpandDuplicates(!expandDuplicates)}
      >
        <DuplicateIcon sx={{ fontSize: 18, color: totalDuplicates > 0 ? 'warning.main' : 'success.main' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          Duplicirani SKU:
        </Typography>
        <Chip
          label={totalDuplicates}
          size="small"
          color={totalDuplicates > 0 ? 'warning' : 'success'}
          sx={{ fontWeight: 'bold' }}
        />
        {stats.duplicateSkus.length > 0 && (
          expandDuplicates ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={expandDuplicates}>
        <List dense sx={{ py: 0, pl: 3 }}>
          {stats.duplicateSkus.map((d) => (
            <ListItem key={d.sku} sx={{ py: 0, px: 0 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" noWrap sx={{ maxWidth: 80 }}>
                      {d.sku}
                    </Typography>
                    <Typography variant="caption" color="warning.main">
                      ×{d.count}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
          {stats.duplicateSkus.length === 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              ... i još
            </Typography>
          )}
        </List>
      </Collapse>
    </Paper>
  );
}
